import React, { useState } from 'react';

import { Typography } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import styled from 'styled-components';

import Checkbox from 'components/lib/Checkbox';
import { isMysteryShopAdmin, isMysteryShopShopper } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { useEditShopRequestModalContext } from './EditShopRequestModalContext';
import { SHOP_REQUEST_PROPAGATION_MODAL_MODE, SHOP_REQUEST_STATUS_LABELS } from '../constants';
import ShopRequestPropagateModal from '../propagate/ShopRequestPropagateModal';

const Container = styled.div`
  display: flex;
  flex-flow: column;
  margin-top: 20px;
  gap: 20px;
`;

const Label = styled(Typography.Text)`
  font-family: var(--font-bold);
`;

const Checkboxes = styled.div`
  display: flex;
  flex-flow: column;
  gap: 10px;
  margin-top: 10px;
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-inner {
    height: 18px;
    width: 18px;
  }
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const CommunitiesCount = styled.span`
  font-size: 14px;
  color: var(--dark-blue);
  cursor: pointer;
  line-height: normal;
`;

const StyledList = styled.ul`
  margin-top: 0;
  padding-left: 28px;
`;

const LivingTypes = styled.div`
  display: flex;
  flex-flow: column;
`;

const Propagation = styled.div`
  display: flex;
  flex-flow: column;
`;

const EditShopRequestModalPricingOptions = () => {
  const { shopRequest, originalShopRequest, setShopRequest } = useEditShopRequestModalContext();
  const { currentUser } = useAppSelector((state) => state.auth);

  const [showPropagationModal, setShowPropagationModal] = useState<boolean>(false);

  const isShopRequestAdmin = currentUser && isMysteryShopAdmin(currentUser);
  const isShopRequestSalesperson = currentUser && isMysteryShopShopper(currentUser);
  const isShopRequestApproved = originalShopRequest?.status === SHOP_REQUEST_STATUS_LABELS.APPROVED;

  const updateCheckbox = (event: CheckboxChangeEvent, property: string) => {
    if (shopRequest) {
      setShopRequest({ ...shopRequest, [property]: event.target.checked });
    }
  };

  const baseCommunity = shopRequest?.requestGroup?.base_community;
  const baseCommunityId = baseCommunity?.id ?? shopRequest?.requestGroup?.base_community_id;
  const shopRequestUnchangedApprovedState =
    shopRequest?.status === SHOP_REQUEST_STATUS_LABELS.APPROVED &&
    originalShopRequest?.status === SHOP_REQUEST_STATUS_LABELS.APPROVED;
  const communities = shopRequestUnchangedApprovedState
    ? shopRequest?.propagation?.communities ?? []
    : shopRequest?.related_communities ?? [];

  return (
    <Container>
      <LivingTypes>
        <Label>Pricing Needed For</Label>
        {isShopRequestAdmin && (
          <Checkboxes>
            <StyledCheckbox
              className={'green'}
              checked={shopRequest?.independentLiving}
              onChange={(e: CheckboxChangeEvent) => updateCheckbox(e, 'independentLiving')}
            >
              <CheckboxLabel>Independent Living</CheckboxLabel>
            </StyledCheckbox>
            <StyledCheckbox
              className={'green'}
              checked={shopRequest?.assistedLiving}
              onChange={(e: CheckboxChangeEvent) => updateCheckbox(e, 'assistedLiving')}
            >
              <CheckboxLabel>Assisted Living</CheckboxLabel>
            </StyledCheckbox>
            <StyledCheckbox
              className={'green'}
              checked={shopRequest?.memoryCare}
              onChange={(e: CheckboxChangeEvent) => updateCheckbox(e, 'memoryCare')}
            >
              <CheckboxLabel>Memory Care</CheckboxLabel>
            </StyledCheckbox>
          </Checkboxes>
        )}
        {isShopRequestSalesperson && (
          <StyledList>
            {shopRequest?.independentLiving && <li>Independent Living</li>}
            {shopRequest?.assistedLiving && <li>Assisted Living</li>}
            {shopRequest?.memoryCare && <li>Memory Care</li>}
          </StyledList>
        )}
      </LivingTypes>
      <Propagation>
        <Label>Propagate To</Label>
        {(isShopRequestSalesperson || (isShopRequestAdmin && isShopRequestApproved)) && (
          <StyledList>
            {shopRequest?.propagation?.propagate_self && <li>Self</li>}
            {shopRequest?.propagation?.propagate_others && (
              <li>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span>Others</span>
                  <CommunitiesCount
                    onClick={(event) => {
                      setShowPropagationModal(true);
                    }}
                  >
                    ({(communities ?? []).length})
                  </CommunitiesCount>
                </div>
              </li>
            )}
            {!shopRequest?.propagation?.propagate_self && !shopRequest?.propagation?.propagate_others && (
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>None Selected</span>
            )}
          </StyledList>
        )}
        {baseCommunity && isShopRequestAdmin && !isShopRequestApproved && (
          <Checkboxes>
            <StyledCheckbox
              className={'green'}
              checked={shopRequest?.propagation?.propagate_self}
              onChange={(e: CheckboxChangeEvent) =>
                setShopRequest({
                  ...shopRequest,
                  propagation: {
                    propagate_self: e.target.checked,
                    propagate_others: shopRequest.propagation?.propagate_others || false,
                  },
                })
              }
            >
              <CheckboxLabel>Self</CheckboxLabel>
            </StyledCheckbox>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <StyledCheckbox
                className={'green'}
                checked={shopRequest?.propagation?.propagate_others}
                onChange={(e: CheckboxChangeEvent) =>
                  setShopRequest({
                    ...shopRequest,
                    propagation: {
                      propagate_self: shopRequest.propagation?.propagate_self || false,
                      propagate_others: e.target.checked,
                    },
                  })
                }
              >
                <CheckboxLabel>
                  <span>Others</span>
                </CheckboxLabel>
              </StyledCheckbox>
              {shopRequest?.propagation?.propagate_others && (
                <CommunitiesCount
                  onClick={() => {
                    setShowPropagationModal(true);
                  }}
                >
                  ({(communities ?? []).length})
                </CommunitiesCount>
              )}
            </div>
          </Checkboxes>
        )}
      </Propagation>
      {showPropagationModal && (
        <ShopRequestPropagateModal
          mode={SHOP_REQUEST_PROPAGATION_MODAL_MODE.PREVIEW}
          subtitle={
            <span>
              <span>Applies to the </span>
              <span style={{ color: 'var(--dark-blue)' }}>{shopRequest?.community?.name}</span>
              <span> mystery shop</span>
            </span>
          }
          baseCommunityId={baseCommunityId}
          communities={communities}
          open={showPropagationModal}
          onCancel={() => setShowPropagationModal(false)}
        />
      )}
    </Container>
  );
};

export default EditShopRequestModalPricingOptions;
